import { supabase } from "@/lib/supabase";
import { callRpc } from "@/lib/api/rpc";

export interface ConnectorRow {
  id: string;
  name: string;
  type: string;
  provider: string | null;
  config: Record<string, unknown> | null;
  status: string;
  enabled: boolean;
  health_status: string;
  auth_status: string;
  connection_status: string;
  external_account_id: string | null;
  marketplace_site: string | null;
  capabilities: Record<string, boolean> | null;
  credential_ref: string | null;
  connection_error: string | null;
  last_sync_at: string | null;
  last_sync_attempt_at: string | null;
  auth_attempted_at?: string | null;
  created_at: string;
}

export interface CarrierRow {
  id: string;
  carrier_code: string;
  name: string;
  config: Record<string, unknown> | null;
  enabled: boolean;
  created_at: string;
}

export interface SyncJobRow {
  id: string;
  connector_id: string | null;
  job_type: string;
  status: string;
  payload: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  error_message: string | null;
  error_class: string | null;
  attempts: number;
  max_attempts: number;
  idempotency_key: string | null;
  next_retry_at: string | null;
  entity_type: string | null;
  entity_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface DomainEventRow {
  id: number;
  event_type: string;
  aggregate_type: string | null;
  aggregate_id: string | null;
  payload: Record<string, unknown> | null;
  status: string;
  error: string | null;
  created_at: string;
  processed_at: string | null;
}

export interface IntegrationExceptionRow {
  id: string;
  connector_id: string | null;
  exception_type: string;
  entity_type: string | null;
  entity_ref: string | null;
  external_data: Record<string, unknown> | null;
  status: string;
  resolution_note: string | null;
  resolved_at: string | null;
  created_at: string;
  first_seen?: string | null;
  last_seen?: string | null;
  occurrence_count?: number | null;
  issue_fingerprint?: string | null;
  superseded_by?: string | null;
}

export interface WebhookEventRow {
  id: string;
  connector_id: string | null;
  provider: string;
  event_id: string;
  topic: string | null;
  status: string;
  error: string | null;
  received_at: string;
  processed_at: string | null;
}

export async function seedIntegrations() {
  return callRpc("seed_integrations_data");
}

export async function registerConnector(params: {
  provider: string;
  name: string;
  orgId: string;
  external_account_id?: string | null;
  marketplace_site?: string | null;
  config?: Record<string, unknown> | null;
  credential_ref?: string | null;
}) {
  return callRpc<{ ok: boolean; id?: string; error?: string }>("register_connector", {
    p_provider: params.provider,
    p_name: params.name,
    p_external_account_id: params.external_account_id ?? null,
    p_marketplace_site: params.marketplace_site ?? null,
    p_config: params.config ?? null,
    p_credential_ref: params.credential_ref ?? null,
    p_org_id: params.orgId,
  });
}

export async function resolveIntegrationException(params: {
  exceptionId: string;
  action: "map" | "ignore" | "resolve";
  entityType?: "product" | "variant";
  entityId?: string;
  externalId?: string;
  note?: string;
}) {
  return callRpc<{ ok: boolean; error?: string }>("resolve_integration_exception", {
    p_exception_id: params.exceptionId,
    p_action: params.action,
    p_entity_type: params.entityType ?? null,
    p_entity_id: params.entityId ?? null,
    p_external_id: params.externalId ?? null,
    p_note: params.note ?? null,
  });
}

export interface MappingTargetRow {
  product_id: string;
  variant_id: string | null;
  product_name: string;
  product_sku: string | null;
  variant_sku: string | null;
  variant_label: string | null;
  status: string;
  matched_field: string;
  match_type: string;
  rank: number;
}

export async function searchMappingTargets(orgId: string, query: string): Promise<MappingTargetRow[]> {
  const data = await callRpc<MappingTargetRow[]>("search_mapping_targets", {
    p_org_id: orgId,
    p_query: query,
    p_limit: 25,
  });
  return Array.isArray(data) ? data : [];
}

export async function testConnectorConnection(connectorId: string) {
  const { data, error } = await supabase.functions.invoke("marketplace-sync", {
    body: { action: "test-connection", connectorId },
  });
  if (error) throw error;
  return data as {
    ok: boolean;
    state?: string;
    error?: string;
    result?: { connected?: boolean };
  };
}

export async function processSyncJobs() {
  const { data, error } = await supabase.functions.invoke("marketplace-sync", {
    body: { action: "process-jobs" },
  });
  if (error) throw error;
  return data as { ok: boolean; processed: number; results: { job_id: string; ok: boolean; errorClass?: string }[] };
}

export async function runSyncProcessor() {
  return callRpc<{ ok: boolean; processed: number; failed: number; deduplicated: number; deferred_to_worker: number }>("run_sync_processor");
}

export async function retrySyncJob(jobId: string) {
  return callRpc<{ ok: boolean; error?: string }>("retry_sync_job", { p_job_id: jobId });
}